import { create } from "zustand";

import { t } from "@hd/i18n/t";
import { showNewMessageNotification } from "@hd/toast/toasts";
import { NotificationsMessage } from "@hd/types/dto/realtime-message/notifications-message.dto";

export interface Notification {
  type: "message" | "connection-info";
  content: NotificationsMessage;
  companyId: number;
}

export interface NotificationsState {
  open: boolean;
  items: Notification[];
  companyId: number | null;
  unreadMessagesCount: number;
  init: (totalUnread: number, notifications: Notification[]) => void;
  push: (notification: Notification) => void;
  toggleOpen: () => void;
  setOpen: (value: boolean) => void;
  setReadAll: () => void;
  setRead: (ids: string[]) => void;
  setCompanyId(companyId: number | null): void;
}

export const useNotificationsStore = create<NotificationsState>()((set) => ({
  open: false,
  items: [],
  companyId: null,
  unreadMessagesCount: 0,

  init: (totalUnread: number, notifications: Notification[]) => {
    set((state) => ({
      ...state,
      unreadMessagesCount: totalUnread,
      items: notifications,
    }));
  },

  push: (notification: Notification) => {
    set((state) => {
      if (
        state.items.some(
          (n) => n.content.contactId == notification.content.contactId
        )
      ) {
        return {
          ...state,
          items: state.items.map((n) =>
            n.content.contactId == notification.content.contactId
              ? notification
              : n
          ),
        };
      } else {
        return {
          ...state,
          unreadMessagesCount: state.unreadMessagesCount + 1,
          items: [...state.items, notification],
        };
      }
    });

    if (notification.type === "message") {
      const content = notification.content as NotificationsMessage;
      showNewMessageNotification(
        content.contactId,
        notification.companyId,
        t("common.newMessageFrom", {
          sender: content.contactName,
        })
      );
    }
  },

  setReadAll: () => {
    set((state) => ({
      ...state,
      unreadMessagesCount: 0,
      items: [],
    }));
  },

  setRead: (ids: string[]) => {
    set((state) => {
      const initialItemsCount = state.items.length;
      const newUnreadedItems = state.items.filter(
        (item) => !ids.includes(item.content.chatMessageId)
      );
      const readItemsCount = initialItemsCount - newUnreadedItems.length;

      return {
        ...state,
        unreadMessagesCount: state.unreadMessagesCount - readItemsCount,
        items: newUnreadedItems,
      };
    });
  },

  toggleOpen: () => {
    set((state) => ({
      ...state,
      open: !state.open,
    }));
  },
  setOpen: (value: boolean) => {
    set((state) => ({
      ...state,
      open: value,
    }));
  },

  setCompanyId: (companyId: number | null) => {
    set((state) => ({
      ...state,
      companyId,
    }));
  },
}));
